import React, { type MouseEvent, useContext } from 'react';

import { mdiPencil } from '@mdi/js';
import Icon from '@mdi/react';
import { Button, Chip, TableCell, TableRow } from '@mui/material';
import moment from 'moment';
import styled from 'styled-components';

import { type AllClasssSessionsData, type AttendanceView, type ClassSessionView, type StudentView } from './types';
import { AttendanceContext, View } from '../../context/AttendanceContext';

const MenuIcon = styled(Icon)`
    max-width: 20px;
    color: ${(props) => props.theme.colors.primary.main};
    height: 25px;
`;

const StyledChip = styled(Chip)`
    border-radius: 4px;
`;

const BodyCell = styled(TableCell)`
    padding: 0.25rem;
`;

export interface AttendanceTableRowProps {
    row: AttendanceView;
    onClick: (event: MouseEvent<HTMLButtonElement>, studentId: string, classSessionId: string) => void;
}

const AttendanceTableRow: React.FC<AttendanceTableRowProps> = ({ row, onClick }) => {
    const { t, view, classSessionId, setStudentId, setClassSessionId } = useContext(AttendanceContext);

    const renderFirstCell = () => {
        switch (view) {
            case View.Session:
                return (row as ClassSessionView).name;
            case View.Student:
                return (row as StudentView).date;
            default:
                return null;
        }
    };

    const renderLastCell = () => {
        switch (view) {
            case View.Session:
                return <Button onClick={() => setStudentId(row.studentId)}>{t('student-report')}</Button>;
            case View.Student:
                return <Button onClick={() => setClassSessionId(row.classSessionId)}>{t('class-report')}</Button>;
            default:
                return null;
        }
    };

    const renderTardyLabel = (isTardy: boolean) => {
        if (!isTardy) return;

        return <StyledChip label={t('tardy')} size={'small'} />;
    };

    const renderAttendanceLabel = (isPresent: boolean) => {
        if (isPresent) {
            return 'Present';
        }

        return <StyledChip label={t('absent')} size={'small'} />;
    };

    const formatTime = (time: string) => {
        if (!time) return;

        return moment(time).format('h:mm A');
    };

    const renderTotalAbsences = () => {
        if (classSessionId !== 'all') return;

        return (
            <BodyCell data-testid="totalAbsences" align={'center'}>
                {(row as AllClasssSessionsData).totalAbsences}
            </BodyCell>
        );
    };

    const handleClick = (e) => {
        onClick(e, row.studentId, row.classSessionId);
    };

    return (
        <TableRow tabIndex={-1}>
            <BodyCell data-testid="name">{renderFirstCell()}</BodyCell>
            <BodyCell data-testid="attendance" align={'center'}>
                {renderAttendanceLabel(row.attendance)}
            </BodyCell>
            <BodyCell data-testid="lateness" align={'center'}>
                {renderTardyLabel(row.tardiness)}
            </BodyCell>
            <BodyCell data-testid="timeIn" align={'center'}>
                {formatTime(row.timeIn)}
            </BodyCell>
            <BodyCell data-testid="timeOut" align={'center'}>
                {formatTime(row.timeOut)}
            </BodyCell>
            {renderTotalAbsences()}
            <BodyCell data-testid="edit" align={'center'} padding={'none'}>
                <Button id="student-actions" size={'small'} onClick={handleClick}>
                    <MenuIcon path={mdiPencil} />
                </Button>
            </BodyCell>
            <BodyCell data-testid="report" align={'center'} padding={'none'}>
                {renderLastCell()}
            </BodyCell>
        </TableRow>
    );
};

export default AttendanceTableRow;
